import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * True North — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/true-north.md §FTUE adapté MVP 2D top-down.
 *   E1 : 1 virage, slow.
 *   E2 : 2 virages.
 *   E3 : 3 virages.
 *
 * MVP — path-2D top-down ; FPS POV motion comes Sprint 3+ (capacitor camera + AR boussole).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { angularError } from './params';
const FIELD = 240;
const CENTER = FIELD / 2;
const ACCEPTANCE_DEMO_DEG = 35;
const SEGMENT_ANIM_MS = 900;
function backBearingFromSegments(segments) {
    let sumX = 0;
    let sumY = 0;
    for (const seg of segments) {
        const rad = (seg.bearing * Math.PI) / 180;
        sumX += Math.sin(rad) * seg.length;
        sumY += -Math.cos(rad) * seg.length;
    }
    let deg = (Math.atan2(-sumX, sumY) * 180) / Math.PI;
    if (deg < 0)
        deg += 360;
    return deg % 360;
}
const RAW_STEPS = [
    {
        idx: 1,
        titleKey: 'true_north.demo.step1_title',
        hintKey: 'true_north.demo.step1_hint',
        segments: [
            { bearing: 0, length: 70 },
            { bearing: 90, length: 60 },
        ],
    },
    {
        idx: 2,
        titleKey: 'true_north.demo.step2_title',
        hintKey: 'true_north.demo.step2_hint',
        segments: [
            { bearing: 0, length: 60 },
            { bearing: 90, length: 70 },
            { bearing: 0, length: 50 },
        ],
    },
    {
        idx: 3,
        titleKey: 'true_north.demo.step3_title',
        hintKey: 'true_north.demo.step3_hint',
        segments: [
            { bearing: 45, length: 60 },
            { bearing: 135, length: 50 },
            { bearing: 90, length: 60 },
            { bearing: 0, length: 50 },
        ],
    },
];
const DEMO_STEPS = RAW_STEPS.map((s) => ({
    ...s,
    trueBackBearing: backBearingFromSegments(s.segments),
}));
function projectPath(segments) {
    const pts = [{ x: 0, y: 0 }];
    let x = 0;
    let y = 0;
    for (const seg of segments) {
        const rad = (seg.bearing * Math.PI) / 180;
        x += Math.sin(rad) * seg.length;
        y += -Math.cos(rad) * seg.length;
        pts.push({ x, y });
    }
    // Centrer dans le field.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
        if (p.x < minX)
            minX = p.x;
        if (p.y < minY)
            minY = p.y;
        if (p.x > maxX)
            maxX = p.x;
        if (p.y > maxY)
            maxY = p.y;
    }
    const padding = 24;
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    const scale = Math.min((FIELD - padding * 2) / w, (FIELD - padding * 2) / h);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    return {
        pts: pts.map((p) => ({
            x: CENTER + (p.x - cx) * scale,
            y: CENTER + (p.y - cy) * scale,
        })),
    };
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing');
    const [walkerIdx, setWalkerIdx] = useState(0);
    const [pickedBearing, setPickedBearing] = useState(null);
    const dialRef = useRef(null);
    const timersRef = useRef([]);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const projected = projectPath(step.segments);
    const errorDeg = pickedBearing === null ? null : angularError(pickedBearing, step.trueBackBearing);
    const correct = errorDeg !== null && errorDeg < ACCEPTANCE_DEMO_DEG;
    useEffect(() => {
        setPhase('showing');
        setWalkerIdx(0);
        setPickedBearing(null);
        for (const t2 of timersRef.current)
            clearTimeout(t2);
        timersRef.current = [];
        for (let i = 1; i <= step.segments.length; i += 1) {
            const idx = i;
            timersRef.current.push(setTimeout(() => {
                setWalkerIdx(idx);
                if (idx === step.segments.length) {
                    timersRef.current.push(setTimeout(() => setPhase('awaiting'), 300));
                }
            }, SEGMENT_ANIM_MS * idx));
        }
        return () => {
            for (const t2 of timersRef.current)
                clearTimeout(t2);
            timersRef.current = [];
        };
    }, [stepIdx, step.segments.length]);
    const computeBearing = useCallback((clientX, clientY) => {
        const node = dialRef.current;
        if (!node)
            return;
        const rect = node.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clientX - cx;
        const dy = clientY - cy;
        let deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
        if (deg < 0)
            deg += 360;
        setPickedBearing(deg);
    }, []);
    const handleConfirm = () => {
        if (phase !== 'awaiting' || pickedBearing === null)
            return;
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    // Pointeur display.
    const SIZE = 200;
    const RADIUS = SIZE / 2;
    const bearingDisplay = pickedBearing ?? 0;
    const tipX = RADIUS + Math.sin((bearingDisplay * Math.PI) / 180) * (RADIUS - 20);
    const tipY = RADIUS + -Math.cos((bearingDisplay * Math.PI) / 180) * (RADIUS - 20);
    // Path polyline shown so far.
    const visibleClamped = Math.min(walkerIdx, projected.pts.length - 1);
    const visiblePts = phase === 'feedback'
        ? projected.pts
        : projected.pts.slice(0, visibleClamped + 1);
    const polyline = visiblePts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const start = projected.pts[0];
    const end = projected.pts[projected.pts.length - 1];
    const walker = projected.pts[visibleClamped] ?? start;
    // Overlay flèches feedback.
    const overlayLength = 60;
    const overlayTip = (deg) => ({
        x: end.x + Math.sin((deg * Math.PI) / 180) * overlayLength,
        y: end.y + -Math.cos((deg * Math.PI) / 180) * overlayLength,
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('true_north.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), phase === 'showing' && (_jsxs("svg", { width: FIELD, height: FIELD, viewBox: `0 0 ${FIELD} ${FIELD}`, className: "border border-cyan-border-faint bg-cyan-surface", "aria-hidden": true, children: [visiblePts.length >= 2 && (_jsx("polyline", { points: polyline, fill: "none", stroke: "#1a3a5c", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" })), _jsx("circle", { cx: start.x, cy: start.y, r: 6, fill: "#0a1628" }), visibleClamped > 0 && (_jsx("circle", { cx: walker.x, cy: walker.y, r: 5, fill: "#1a3a5c" }))] })), phase === 'awaiting' && (_jsxs("div", { className: "flex flex-col items-center gap-s3", children: [_jsx("p", { className: "text-[13px] text-cyan-text-secondary", children: t('true_north.demo.ask_back') }), _jsxs("div", { ref: dialRef, onPointerDown: (e) => {
                            e.target.setPointerCapture?.(e.pointerId);
                            computeBearing(e.clientX, e.clientY);
                        }, onPointerMove: (e) => {
                            if (e.buttons === 0 && e.pointerType === 'mouse')
                                return;
                            computeBearing(e.clientX, e.clientY);
                        }, className: "relative touch-none rounded-full border border-cyan-border bg-cyan-surface", style: { width: SIZE, height: SIZE }, children: [['n', 'e', 's', 'w'].map((dir, i) => {
                                const angle = i * 90;
                                const x = RADIUS + Math.sin((angle * Math.PI) / 180) * (RADIUS - 12);
                                const y = RADIUS + -Math.cos((angle * Math.PI) / 180) * (RADIUS - 12);
                                return (_jsx("span", { className: "pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 font-mono text-[11px] uppercase text-cyan-text", style: { left: x, top: y }, children: t(`true_north.buttons.${dir}_label`) }, dir));
                            }), _jsxs("svg", { width: SIZE, height: SIZE, viewBox: `0 0 ${SIZE} ${SIZE}`, className: "pointer-events-none absolute inset-0", children: [_jsx("line", { x1: RADIUS, y1: RADIUS, x2: tipX, y2: tipY, stroke: "#0a1628", strokeWidth: 4, strokeLinecap: "round" }), _jsx("circle", { cx: RADIUS, cy: RADIUS, r: 6, fill: "#1a3a5c" }), _jsx("circle", { cx: tipX, cy: tipY, r: 5, fill: "#0a1628" })] })] }), _jsx("button", { type: "button", onClick: handleConfirm, disabled: pickedBearing === null, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network disabled:cursor-not-allowed disabled:opacity-50", children: t('true_north.buttons.confirm') })] })), phase === 'feedback' && pickedBearing !== null && errorDeg !== null && (_jsxs(_Fragment, { children: [_jsxs("svg", { width: FIELD, height: FIELD, viewBox: `0 0 ${FIELD} ${FIELD}`, className: "border border-cyan-border-faint bg-cyan-surface", "aria-hidden": true, children: [_jsx("polyline", { points: polyline, fill: "none", stroke: "#1a3a5c", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("circle", { cx: start.x, cy: start.y, r: 6, fill: "#0a1628" }), _jsx("circle", { cx: end.x, cy: end.y, r: 5, fill: "#1a3a5c" }), (() => {
                                const tp = overlayTip(pickedBearing);
                                return (_jsx("line", { x1: end.x, y1: end.y, x2: tp.x, y2: tp.y, stroke: "#3a78b5", strokeWidth: 3, strokeLinecap: "round" }));
                            })(), (() => {
                                const tp = overlayTip(step.trueBackBearing);
                                return (_jsx("line", { x1: end.x, y1: end.y, x2: tp.x, y2: tp.y, stroke: "#0a1628", strokeWidth: 3, strokeLinecap: "round", strokeDasharray: "4 3" }));
                            })()] }), _jsx("p", { className: [
                            'text-[13px]',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: correct
                            ? t('true_north.demo.feedback_correct', { error: Math.round(errorDeg) })
                            : t('true_north.demo.feedback_incorrect', { error: Math.round(errorDeg) }) })] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map